// @ts-check
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import every from 'lodash/every';
import toNumber from 'lodash/toNumber';
import reduce from 'lodash/reduce';
import constants from './constants';
import events from './events';
import { init as cartInit } from './cart_unified/cart-unified';
import optionsSuccess from './utilities/options-success';
import optionsError from './utilities/options-error';
import addClass from './utilities/dom/add-class';
import removeClass from './utilities/dom/remove-class';
import { getShopifyCart } from './cart_unified/utilities';
import {
  setCheckoutItems,
  isEnabled as isBlazeEnabled,
} from './blaze';

const defaults = {
  cartItemQuantity: 1,
};

export default class CartElements {
  constructor() {
    this.cartDomInstances = [];
    this.selectElements();
    this.flowcalizingTimerId = null;
    this.cart = cartInit({
      flowApiBaseURI: 'https://api.flow.io',
    });
  }

  static getLineItemDiscounts(cartItemElement) {
    const discountAmount = cartItemElement.getAttribute(constants.CART_ITEM_DISCOUNT_AMOUNT);
    const discountCurrency = cartItemElement.getAttribute(constants.CART_ITEM_DISCOUNT_CURRENCY);
    const discountPercent = cartItemElement.getAttribute(constants.CART_ITEM_DISCOUNT_PERCENT);

    if (discountAmount && discountCurrency) {
      return {
        discounts: [{
          offer: {
            discriminator: 'discount_offer_fixed',
            money: {
              amount: toNumber(discountAmount),
              currency: discountCurrency,
            },
          },
        }],
      };
    }

    if (discountPercent) {
      return {
        discounts: [{
          offer: {
            discriminator: 'discount_offer_percent',
            percent: toNumber(discountPercent),
          },
        }],
      };
    }

    return undefined;
  }

  selectElements() {
    const cartContainerElements = [].slice.call(document.querySelectorAll(`[${constants.CART_CONTAINER}]`));
    this.cartDomInstances = [];

    if (cartContainerElements.length === 0) return;

    forEach(cartContainerElements, (cartContainerElement) => {
      const cartItemNodeList = cartContainerElement.querySelectorAll(`[${constants.CART_ITEM_NUMBER}]`);
      const cartElements = Array.prototype.slice.call(cartItemNodeList);

      this.cartDomInstances.push({
        cartItemsList: reduce(cartElements, (cartItems, cartItem) => {
          const cartItemNumber = cartItem.getAttribute(constants.CART_ITEM_NUMBER);
          const cartItemQuantity = (
            cartItem.getAttribute(constants.CART_ITEM_QUANTITY)
            || defaults.cartItemQuantity
          );
          const cartItemElement = cartItem;
          const cartItemPriceElements = cartItem.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_ITEM_PRICE}"]`);
          const cartItemLineTotalElements = cartItem.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_ITEM_LINE_TOTAL}"]`);
          const cartItemCompareAtElements = cartItem.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_ITEM_COMPARE_AT}"]`);
          const cartItemDiscounts = CartElements.getLineItemDiscounts(cartItem);

          return [...cartItems, {
            cartItemNumber,
            cartItemQuantity,
            cartItemElement,
            cartItemPriceElements,
            cartItemLineTotalElements,
            cartItemDiscounts,
            cartItemCompareAtElements,
          }];
        }, []),
        discountAmount: cartContainerElement.getAttribute(constants.CART_DISCOUNT_AMOUNT),
        discountCurrency: cartContainerElement.getAttribute(constants.CART_DISCOUNT_CURRENCY),
        cartSummaryElements: {
          cartTaxNameElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_TAX_NAME}"]`),
          cartTaxElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_TAX}"]`),
          cartDiscountElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_DISCOUNT}"]`),
          cartDutyElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_DUTY}"]`),
          cartShippingElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_SHIPPING}"]`),
          cartSubtotalElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_SUBTOTAL}"]`),
          cartTotalElements: cartContainerElement.querySelectorAll(`[${constants.LOCALIZE}="${constants.CART_TOTAL}"]`),
        },
      });
    });
  }

  /**
   * @description Sets a class on the cart container to indicate that prices are in the
   * process of being localized. Will automatically remove the class after timeout if
   * localizing does not succeed.
   * @param {object} [options]
   * @param {boolean} [options.toggle] boolean as to wehter to add or remove the class
   * defaults to true (add class)
   */
  setFlowcalizingState(options = {}) {
    const enable = options.toggle !== false;
    const classModifierFunc = enable ? addClass : removeClass;
    const cartContainerElements = [].slice.call(document.querySelectorAll(`[${constants.CART_CONTAINER}]`));
    forEach(cartContainerElements, (cartContainer) => {
      classModifierFunc(cartContainer, constants.CART_LOADING_CLASS);
    });

    if (enable) {
      // disable state after 5 seconds if localizing fails for any reason
      this.flowcalizingTimerId = setTimeout(() => {
        this.setFlowcalizingState({ toggle: false });
      }, 5000);
    } else {
      clearTimeout(this.flowcalizingTimerId);
      this.flowcalizingTimerId = null;
    }
  }

  updatePrices(
    /** @type {import('@flowio/types/dist/api').Order} */
    order,
  ) {
    const orderPrices = get(order, 'prices');
    const orderLineItems = get(order, 'lines');
    const orderItems = get(order, 'items');

    events.trigger(events.constants.WILL_LOCALIZE_CART, { order });

    forEach(this.cartDomInstances, (cartDomInstance) => {
      // localize item prices
      forEach(cartDomInstance.cartItemsList, (cartItem) => {
        const {
          cartItemPriceElements,
          cartItemLineTotalElements,
          cartItemCompareAtElements,
        } = cartItem;

        const orderLineItem = find(
          orderLineItems,
          line => line.item_number === cartItem.cartItemNumber,
        );
        const orderItem = find(
          orderItems,
          item => item.number === cartItem.cartItemNumber
            && every(item.attributes, (val, key) => val === orderLineItem.attributes[key]),
        );
        const priceLabel = get(orderLineItem, 'price.label');
        const totalLabel = get(orderLineItem, 'total.label');
        const compareAtLabel = get(orderItem, 'local.price_attributes.compare_at.label');

        if (!isNil(priceLabel)) {
          forEach(cartItemPriceElements, (priceElement) => {
            priceElement.innerText = priceLabel; // eslint-disable-line no-param-reassign
          });
        }

        if (!isNil(totalLabel)) {
          forEach(cartItemLineTotalElements, (lineTotalElement) => {
            lineTotalElement.innerText = totalLabel; // eslint-disable-line no-param-reassign
          });
        }

        if (!isNil(compareAtLabel)) {
          forEach(cartItemCompareAtElements, (compareAtElement) => {
            compareAtElement.innerText = compareAtLabel; // eslint-disable-line no-param-reassign
          });
        }
      });

      events.trigger(events.constants.CART_ITEM_PRICES_LOCALIZED, { order });

      // localize cart summary prices
      const cartTax = find(orderPrices, price => price.key === 'vat');
      const cartTaxName = get(order.attributes, 'flow_vat_name') || get(cartTax, 'name');
      const cartTaxAmount = get(cartTax, 'label');
      const cartDuty = get(find(orderPrices, price => price.key === 'duty'), 'label');
      const cartDiscount = get(find(orderPrices, price => price.key === 'discount'), 'label');
      const cartShipping = get(find(orderPrices, price => price.key === 'shipping'), 'label');
      const cartSubtotal = get(find(orderPrices, price => price.key === 'subtotal'), 'label');
      const cartTotal = get(order, 'total.label');

      if (!isNil(cartTaxName)) {
        forEach(cartDomInstance.cartSummaryElements.cartTaxNameElements, (summaryElement) => {
          summaryElement.innerText = cartTaxName; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartTaxAmount)) {
        forEach(cartDomInstance.cartSummaryElements.cartTaxElements, (summaryElement) => {
          summaryElement.innerText = cartTaxAmount; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartDuty)) {
        forEach(cartDomInstance.cartSummaryElements.cartDutyElements, (summaryElement) => {
          summaryElement.innerText = cartDuty; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartDiscount)) {
        forEach(cartDomInstance.cartSummaryElements.cartDiscountElements, (summaryElement) => {
          summaryElement.innerText = cartDiscount; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartShipping)) {
        forEach(cartDomInstance.cartSummaryElements.cartShippingElements, (summaryElement) => {
          summaryElement.innerText = cartShipping; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartSubtotal)) {
        forEach(cartDomInstance.cartSummaryElements.cartSubtotalElements, (summaryElement) => {
          summaryElement.innerText = cartSubtotal; // eslint-disable-line no-param-reassign
        });
      }

      if (!isNil(cartTotal)) {
        forEach(cartDomInstance.cartSummaryElements.cartTotalElements, (summaryElement) => {
          summaryElement.innerText = cartTotal; // eslint-disable-line no-param-reassign
        });
      }
    });

    events.trigger(events.constants.CART_SUMMARY_PRICES_LOCALIZED, { order });
    events.trigger(events.constants.CART_LOCALIZED, { order });
    this.setFlowcalizingState({ toggle: false });
  }

  /**
   * @description Localize the cart(s) on the current page
   */
  localize(options = {}) {
    this.setFlowcalizingState();
    return getShopifyCart(options.cart).then(shopifyCart =>
      new Promise((resolve, reject) => {
        // refesh elements to make sure we have the latest.
        this.selectElements();
        this.cart.shopifyCart.getFlowOrderFromCart(shopifyCart, {
          onSuccess: (response) => {
            /** @type {import('@flowio/types/dist/api').Order} */
            const order = get(response, 'flow.order');
            this.updatePrices(order);
            optionsSuccess(options, response);
            resolve(response);
            isBlazeEnabled().then((enabled) => {
              if (enabled) {
                setCheckoutItems(order.items.map(item => ({
                  number: item.number,
                  quantity: item.quantity,
                  attributes: item.attributes,
                })));
              }
            });
          },
          onError: (error) => {
            optionsError(options, error);
            reject(error);
          },
        });
      }))
      .catch((error) => {
        optionsError(options, error);

        // pass the error up to caller
        Promise.reject(error);
      });
  }
}



// WEBPACK FOOTER //
// ./static/js/v0/cart-elements.js